import React, { forwardRef, useEffect, useMemo } from 'react'
import {
  IndexPath,
  Select,
  Select as KittenSelect,
  SelectItem,
  SelectProps,
} from '@ui-kitten/components'
import isObject from 'lodash/isObject'
import { RenderProp } from '@ui-kitten/components/devsupport'
import { ImageProps } from 'react-native'
import { CloseIcon } from './BtInput'
import { composeStyle, createEditable } from '../helpers'
import { useResponsive } from '../hooks'
import { BtInputLabel } from './BtInputLabel'

interface ISelectProps extends Omit<SelectProps, 'onSelect' | 'label'> {
  options?: any[]
  errorText?: string
  keyName?: string
  labelName?: string
  onChange?: ((item: any) => void) | null
  onClearPress?: ((item: any) => void) | null
  label?: string
  value?: any
  sort?: boolean
  primitiveArray?: boolean
  mandatory?: boolean
  model?: any
  name?: string
  clearable?: boolean
  testID?: string
}

const BtSelect_ = React.memo(
  forwardRef<Select, ISelectProps>(
    (
      {
        options = [],
        value,
        errorText,
        caption,
        keyName = 'id',
        labelName = 'name',
        label,
        onChange,
        sort = false,
        primitiveArray = false,
        mandatory = false,
        status,
        clearable = false,
        multiSelect,
        style,
        size,
        testID = 'default_test_id_select',
        ...rest
      }: ISelectProps,
      ref: React.Ref<Select>,
    ) => {
      const [selectedIndex, setSelectedIndex] = React.useState<
        IndexPath | IndexPath[] | undefined
      >(undefined)

      useEffect(() => {
        if (value === null || value === undefined) {
          setSelectedIndex(undefined)
        } else {
          if (isObject(value)) {
            if (multiSelect) {
              const values = value as Array<any>
              const indexes: IndexPath[] = []
              values.map((v) => {
                const index = primitiveArray
                  ? options?.findIndex((option) => option === v)
                  : options.findIndex(
                      (option) => option[keyName] === value[keyName],
                    )
                if (index >= 0) {
                  indexes.push(new IndexPath(index))
                }
              })
              setSelectedIndex(indexes)
            } else {
              const index = options.findIndex(
                (option) => option[keyName] === value[keyName],
              )
              setSelectedIndex(new IndexPath(index))
            }
          } else {
            if (!multiSelect) {
              const index = primitiveArray
                ? options?.findIndex((option) => option === value)
                : options.findIndex((option) => option[keyName] === value)
              setSelectedIndex(new IndexPath(index))
            } else {
              setSelectedIndex([])
            }
          }
        }
      }, [keyName, multiSelect, options, primitiveArray, value])

      let displayValue

      if (selectedIndex && options && options.length > 0) {
        if (multiSelect && (selectedIndex as IndexPath[]).length > 0) {
          const values: any[] = []
          const indexes = selectedIndex as IndexPath[]
          indexes.forEach((si) => {
            if ((si as IndexPath).row >= 0) {
              const val = primitiveArray
                ? options[(si as IndexPath).row]
                : options[(si as IndexPath).row]?.[labelName]
              values.push(val)
            }
          })
          displayValue = values.join('; ')
        } else {
          displayValue = primitiveArray
            ? options[(selectedIndex as IndexPath).row]
            : options[(selectedIndex as IndexPath).row]?.[labelName]
        }
      }

      const clearButton = useMemo<
        RenderProp<Partial<ImageProps>> | undefined
      >(() => {
        if (!selectedIndex || !clearable) return undefined
        return (
          <CloseIcon
            onPress={() => {
              setSelectedIndex(undefined)
              if (onChange) {
                onChange(multiSelect ? [] : null)
              }
            }}
          />
        )
      }, [clearable, multiSelect, onChange, selectedIndex])

      const renderItems = useMemo(() => {
        const items = sort
          ? options.sort((a, b) =>
              primitiveArray
                ? a.localeCompare(b)
                : a[labelName].localeCompare(b[labelName]),
            )
          : options
        return items.map((o, i) => (
          <SelectItem
            testID={`${testID}-${i}`}
            key={i}
            title={primitiveArray ? o : o[labelName]}
          />
        ))
      }, [labelName, options, primitiveArray, sort, testID])

      let _selectedIndex
      if (selectedIndex) {
        _selectedIndex = multiSelect
          ? selectedIndex
          : (selectedIndex as IndexPath).row >= 0
          ? selectedIndex
          : undefined
      }

      const { sm } = useResponsive()

      return (
        <KittenSelect
          caption={errorText ? errorText : caption}
          status={errorText ? 'danger' : status || 'basic'}
          accessoryRight={clearButton}
          value={displayValue}
          size={size ? size : sm ? 'large' : 'medium'}
          placeholder={rest.placeholder || ' '}
          selectedIndex={_selectedIndex}
          onSelect={(index) => {
            setSelectedIndex(index)
            if (onChange) {
              if (multiSelect) {
                const values: any[] = []
                const arr: IndexPath[] = index as IndexPath[]
                arr.map((i) => {
                  values.push(options[(i as IndexPath).row])
                })
                onChange(values)
              } else {
                onChange(options[(index as IndexPath).row])
              }
            }
          }}
          label={<BtInputLabel required={mandatory}>{label}</BtInputLabel>}
          multiSelect={multiSelect}
          style={composeStyle({ minWidth: 100 }, style)}
          ref={ref}
          testID={testID}
          {...rest}
        >
          {renderItems}
        </KittenSelect>
      )
    },
  ),
)

export const BtSelect = createEditable({ Component: BtSelect_ })
